import type { FC } from "react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { GKButton } from "@granular/gds";

import { LoginCardLayout } from "../../LoginCardLayout";
import {
  clearInProgressInteraction,
  getAuthEnv,
  makeAuthApp,
} from "../../../services/auth/new-msal/init";
import { AuthSource, getAuthConfig } from "@granular/fabric3-definitions";
import { useMount } from "../../../hooks/mount";
import { doLoginRedirect } from "../../../services/auth/new-msal/redirect";
import { useFeatureFlags } from "../../../hooks/featureFlags";
import type { LoginFeatureFlags } from "../LoginFeatureFlags";

type LoadingState = "initializing" | AuthSource | undefined;

export const SelfIdFlow: FC = () => {
  /**
   * If this anything other than undefined, we're in a loading state and should block interaction.
   *
   * If it's "b2b", "b2c", or "vylor_b2b", we're working on a redirect for that source, so its button should say "Signing in..."
   */
  const [signinLoading, setSigninLoading] = useState<LoadingState>();
  const isLoading = signinLoading !== undefined;
  const showVylorSSO =
    new URLSearchParams(globalThis.location.search).get("enableVylorSSO") !==
    "false";

  /**
   * Corteva sign-in is gated so it can be switched off ahead of the tenant
   * being retired (CAT-7338; removed outright in CAT-7323).
   *
   * The flag is read from an anonymous LaunchDarkly context that initializes
   * on mount, so it is `undefined` on the first render and stays that way if LD
   * is slow or unreachable. We therefore hide the button only on an explicit
   * `true`: Corteva is still a live employee path, and treating "not resolved"
   * as "hide" would drop it for anyone whose LD request hasn't landed.
   */
  const { "app-login-hide-corteva-button": hideCortevaSSO } =
    useFeatureFlags<LoginFeatureFlags>();
  const showCortevaSSO = hideCortevaSSO !== true;

  useMount(() => {
    /**
     * Clear out any in-progress interactions from previous attempts. This avoids
     * "interaction_in_progress" errors when the user has followed one of these auth links and used
     * the browser back button.
     */
    setSigninLoading("initializing");
    void (async () => {
      try {
        await Promise.all([
          clearInProgressInteraction(getAuthConfig(getAuthEnv(), "b2b")),
          clearInProgressInteraction(getAuthConfig(getAuthEnv(), "b2c")),
          clearInProgressInteraction(getAuthConfig(getAuthEnv(), "vylor_b2b")),
        ]);
      } finally {
        setSigninLoading(undefined);
      }
    })();
  });

  const handleMemberLogin = async (
    tenantType: "vylor_b2b" | "b2c" | "b2b",
  ): Promise<void> => {
    // Prevent multiple simultaneous authentication attempts
    if (isLoading) {
      return;
    }
    setSigninLoading(tenantType);

    try {
      const authConfig = getAuthConfig(getAuthEnv(), tenantType);
      const authApp = await makeAuthApp(authConfig);

      /**
       * We don't pass an active account here, because if there is one,
       * we probably detected it during the identify flow, and we want
       * to force the user to either pick a different account or
       * re-authenticate.
       *
       * Note: The redirectUri parameter is currently unused. Post-login
       * navigation is handled via sessionStorage. See containerHooks.ts.
       */
      const activeAccount = undefined;
      await doLoginRedirect(authApp, authConfig, activeAccount);
      setSigninLoading(undefined);
    } catch (error) {
      console.error(`Login failed for ${tenantType}:`, error);
      // Reset state on error so user can try again
      setSigninLoading(undefined);
      // If it's an interaction_in_progress error, provide specific guidance
      if (
        error instanceof Error &&
        error.message.includes("interaction_in_progress")
      ) {
        console.warn(
          "Authentication interaction already in progress. Please wait for the current authentication to complete.",
        );
      }
    }
  };

  return (
    <LoginCardLayout>
      {showVylorSSO && (
        <GKButton
          data-qa="sign_in_as_vylor_team_member"
          data-testid="sign_in_as_vylor_team_member"
          onClick={() => void handleMemberLogin("vylor_b2b")}
          disabled={isLoading}
        >
          {signinLoading === "vylor_b2b" ? (
            <FormattedMessage id="signing_in" defaultMessage="Signing in..." />
          ) : (
            <FormattedMessage id="sign_in_as_vylor_team_member" />
          )}
        </GKButton>
      )}
      {showCortevaSSO && (
        <GKButton
          className="mt-4"
          data-qa="sign_in_as_corteva_team_member"
          data-testid="sign_in_as_corteva_team_member"
          onClick={() => void handleMemberLogin("b2b")}
          disabled={isLoading}
        >
          {signinLoading === "b2b" ? (
            <FormattedMessage id="signing_in" defaultMessage="Signing in..." />
          ) : (
            <FormattedMessage id="sign_in_as_corteva_team_member" />
          )}
        </GKButton>
      )}
      <GKButton
        className="mt-4"
        data-qa="signing_in_b2c"
        data-testid="signing_in_b2c"
        onClick={() => void handleMemberLogin("b2c")}
        disabled={isLoading}
      >
        {signinLoading === "b2c" ? (
          <FormattedMessage id="signing_in" defaultMessage="Signing in..." />
        ) : (
          <FormattedMessage id="sign_in_as_grower" />
        )}
      </GKButton>
    </LoginCardLayout>
  );
};
